import { defineStore } from 'pinia';
import { JWTHttpClient } from '@/services/JWTHttpClient';
import type { CopilotSearchResult } from '@/types/copilot';
import type { UserContext } from '@/composables/useUserContext';
import type { StreamEvent } from '@/types/streaming';

// Create JWT HTTP client for CoPilot API
const copilotJWTClient = new JWTHttpClient(import.meta.env.VITE_COPILOT_API_HOST || 'https://genai.ovationcxm.ai');

const NODE_LABELS: Record<string, string> = {
    initialize: 'Starting...',
    preprocess: 'Preparing...',
    retrieve: 'Searching knowledge base...',
    retrieval_tools: 'Running retrieval tools...',
    generate: 'Generating response...',
    execute_tools: 'Using tools...',
    postprocess: 'Processing...',
    check_triggers: 'Checking triggers...',
    finalize: 'Finalizing...',
};

function formatToolName(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const _copilotBase = import.meta.env.VITE_COPILOT_API_HOST || 'https://genai.ovationcxm.ai';

export const useCopilotStore = defineStore('copilot', {
    state: () => {
        return {
            searchUrl: `${_copilotBase}/agents/run/stream`,
            streamUrl: `${_copilotBase}/agents/run/stream`,
            _activeReader: null as ReadableStreamDefaultReader<Uint8Array> | null,
            _streamCancelled: false,
        };
    },
    getters: {},
    actions: {
        setCopilotUrls(urls: { searchUrl: string; streamUrl: string }) {
            this.searchUrl = urls.searchUrl;
            this.streamUrl = urls.streamUrl;
        },

        cancelStream() {
            this._streamCancelled = true;
            this._activeReader?.cancel();
            this._activeReader = null;
        },
        
        async submitSearch(
            searchData: string | { 
                query: string; 
                context?: any; 
                session_id?: string; 
                userContext?: UserContext
                // Deprecated: user_id (use userContext instead)
                user_id?: string;
            }, 
            onContentUpdate?: (content: string) => void,
            onEvent?: (event: StreamEvent) => void,
        ): Promise<CopilotSearchResult> {
            this._streamCancelled = false;

            // Track tool start times for duration computation
            const toolStartTs: Record<string, number> = {};

            const emitEvent = (event: StreamEvent) => {
                if (onEvent) onEvent(event);
            };

            let contentStarted = false;

            const handleParsed = (parsed: any): { done: boolean; error?: string } => {
                const ts: number = typeof parsed.ts === 'number' ? parsed.ts : Date.now() / 1000;

                switch (parsed.type) {
                    case 'started':
                        emitEvent({ type: 'started', label: 'Connected', status: 'info', ts });
                        break;

                    case 'node_start': {
                        const node = parsed.node as string;
                        // Use backend-supplied label when present (e.g. "Analyzing user input" / "Analyzing tool response" on generate node)
                        const label = parsed.label || NODE_LABELS[node] || `${node}...`;
                        emitEvent({ type: 'node_start', label, status: 'running', ts, detail: node });
                        break;
                    }

                    case 'node_end': {
                        const node = parsed.node as string;
                        const baseLabel = NODE_LABELS[node] || node;
                        const sourceCount: number | undefined = parsed.source_count;
                        // Backend emits source_count on BOTH `retrieve` (vector-only)
                        // and `retrieval_tools` (vector+tool RRF-fused total). When
                        // vector hits are filtered by similarity threshold but ES
                        // tools later return matches, the retrieve node ends at
                        // "Found 0 sources" and retrieval_tools ends at the true
                        // post-fusion total — surface the count for both so the UI
                        // reflects what the user actually got.
                        const showSourceCount =
                            (node === 'retrieve' || node === 'retrieval_tools')
                            && sourceCount != null;
                        const label = showSourceCount
                            ? `Found ${sourceCount} source${sourceCount !== 1 ? 's' : ''}`
                            : baseLabel.replace(/\.\.\.$/, '');
                        emitEvent({ type: 'node_end', label, status: 'done', ts, detail: node });
                        break;
                    }

                    case 'workflow_event': {
                        const status = parsed.status as string;
                        const wfIds: string[] = parsed.workflow_ids || [];
                        const steps = parsed.steps_executed ?? 0;
                        const wfNamesMap: Record<string, string> = parsed.workflow_names ?? {};
                        const unresolved: string[] = Array.isArray(parsed.unresolved_template_vars)
                            ? parsed.unresolved_template_vars : [];

                        const validNames = wfIds.map(id => wfNamesMap[id] || null).filter(Boolean) as string[];
                        const stepsTag = steps === 1 ? '1 step' : `${steps} steps`;
                        let actionBase: string;
                        switch (status) {
                            case 'ok': actionBase = `Workflow ran (${stepsTag})`; break;
                            case 'ok_escalation': actionBase = `Workflow escalated (${stepsTag})`; break;
                            case 'no_response': actionBase = `Workflow ran — no reply produced (${stepsTag})`; break;
                            case 'failed': actionBase = `Workflow failed: ${parsed.error || 'unknown error'}`; break;
                            case 'skipped_inactive': actionBase = `Workflow skipped (inactive)`; break;
                            case 'not_run': actionBase = `Workflow couldn't run`; break;
                            default: actionBase = `Workflow: ${status}`;
                        }
                        const label = validNames.length > 0
                            ? `${actionBase} — ${validNames.join(', ')}`
                            : actionBase;

                        const detailLines: string[] = [];
                        wfIds.forEach(id => {
                            detailLines.push(id);
                        });
                        detailLines.push(`Hook: ${parsed.hook || 'unknown'}`);
                        if (parsed.error) detailLines.push(`Error: ${parsed.error}`);
                        if (unresolved.length > 0) {
                            detailLines.push(`Unresolved vars: ${unresolved.map((v: string) => `{{${v}}}`).join(', ')}`);
                        }

                        const evtStatus = status === 'failed' ? 'error' : unresolved.length > 0 ? 'error' : 'info';
                        emitEvent({ type: 'workflow_event', label, status: evtStatus, ts, detail: detailLines.join('\n') });
                        break;
                    }

                    case 'tool_start': {
                        const name = parsed.display_name || formatToolName(parsed.name || 'tool');
                        toolStartTs[parsed.id] = ts;
                        emitEvent({
                            type: 'tool_start',
                            label: name,
                            status: 'running',
                            ts,
                            id: parsed.id,
                            detail: parsed.input != null ? JSON.stringify(parsed.input).slice(0, 300) : undefined,
                        });
                        break;
                    }

                    case 'tool_end': {
                        const name = parsed.display_name || formatToolName(parsed.name || 'tool');
                        // duration_ms from backend is the most accurate; ts delta is unreliable
                        // because tool_start and tool_end can share the same ts when batched
                        const durMs = typeof parsed.duration_ms === 'number'
                            ? Math.round(parsed.duration_ms)
                            : toolStartTs[parsed.id] != null
                                ? Math.round((ts - toolStartTs[parsed.id]) * 1000)
                                : null;
                        const durLabel = durMs != null
                            ? (durMs < 1000 ? ` (${durMs}ms)` : ` (${(durMs / 1000).toFixed(1)}s)`)
                            : '';
                        emitEvent({
                            type: 'tool_end',
                            label: `${name}${durLabel}`,
                            status: 'done',
                            ts,
                            id: parsed.id,
                            detail: parsed.output != null ? String(parsed.output).slice(0, 300) : undefined,
                        });
                        break;
                    }

                    case 'tool_error': {
                        const name = parsed.display_name || formatToolName(parsed.name || 'tool');
                        emitEvent({
                            type: 'tool_error',
                            label: `${name} failed`,
                            status: 'error',
                            ts,
                            detail: parsed.error,
                        });
                        break;
                    }

                    case 'error': {
                        const backendMsg = parsed.error || 'An error occurred';
                        console.error('[copilot stream] Backend error event:', backendMsg);
                        return { done: true, error: backendMsg };
                    }

                    case 'content':
                        if (!contentStarted) {
                            contentStarted = true;
                            emitEvent({ type: 'content_start', label: 'Responding', status: 'info', ts });
                        }
                        return { done: false };

                    case 'trace':
                        return { done: false };

                    case 'metadata':
                        return { done: false };

                    case 'complete':
                        return { done: true };

                    case 'done':
                        return { done: true };

                    default:
                        break;
                }

                return { done: false };
            };

            try {
                // Handle both string and structured data formats
                const data = typeof searchData === 'string' 
                    ? { query: searchData }
                    : { 
                        query: searchData.query,
                        ...(searchData.context && { context: searchData.context }),
                        ...(searchData.session_id && { session_id: searchData.session_id }),
                        ...(searchData.userContext && { userContext: searchData.userContext }),
                        ...(searchData.user_id && { user_id: searchData.user_id })
                      };
                
                const url = this.streamUrl;

                // Parse the full stream URL into base + path so we can create
                // a correctly-scoped client instead of relying on the module-level
                // singleton (which may have been initialised before the env var
                // resolved, or before setCopilotUrls was called).
                const parsedUrl = new URL(url);
                const streamBase = parsedUrl.origin;
                const streamPath = parsedUrl.pathname + parsedUrl.search;
                const streamClient = new JWTHttpClient(streamBase);

                // Use JWT HTTP client for streaming request
                const response = await streamClient.postStream(streamPath, data);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error('Failed to get response reader');
                }

                this._activeReader = reader;

                const decoder = new TextDecoder();
                let content = '';
                let metadata: any = null;
                let traceId: string | null = null;
                let returnedSessionId: string | null = null;
                let buffer = '';
                let streamError: string | null = null;
                
                try {
                    outer: while (true) {
                        const { done, value } = await reader.read();
                        
                        if (done || this._streamCancelled) break;
                        
                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        
                        // Split by lines and process each complete line
                        const lines = buffer.split('\n');
                        // Keep the last potentially incomplete line in buffer
                        buffer = lines.pop() || '';
                        
                        for (const line of lines) {
                            const trimmedLine = line.trim();
                            if (!trimmedLine) continue;
                            
                            let parsed;
                            try {
                                if (trimmedLine.startsWith('data: ')) {
                                    const jsonData = trimmedLine.slice(6);
                                    if (jsonData.trim() === '[DONE]') break outer;
                                    parsed = JSON.parse(jsonData);
                                } else {
                                    parsed = JSON.parse(trimmedLine);
                                }
                            } catch {
                                console.warn('Failed to parse line:', trimmedLine);
                                continue;
                            }

                            // Handle content/trace/metadata outside handleParsed for accumulation
                            if (parsed.type === 'content') {
                                content += parsed.content || '';
                                if (onContentUpdate) onContentUpdate(content);
                                continue;
                            }
                            if (parsed.type === 'trace') {
                                traceId = parsed.trace_id || null;
                                continue;
                            }
                            if (parsed.type === 'metadata') {
                                metadata = parsed;
                                continue;
                            }
                            if (parsed.type === 'done') {
                                returnedSessionId = parsed.session_id || null;
                                handleParsed(parsed);
                                break outer;
                            }

                            const result = handleParsed(parsed);
                            if (result.done) {
                                if (result.error) streamError = result.error;
                                break outer;
                            }
                        }
                    }
                    
                    // Process any remaining buffer
                    if (buffer.trim()) {
                        let parsed;
                        try {
                            parsed = JSON.parse(buffer.trim());
                        } catch {
                            console.warn('Failed to parse final buffer:', buffer);
                        }
                        if (parsed) {
                            if (parsed.type === 'content') {
                                content += parsed.content || '';
                                if (onContentUpdate) onContentUpdate(content);
                            } else if (parsed.type === 'trace') {
                                traceId = parsed.trace_id || null;
                            } else if (parsed.type === 'metadata') {
                                metadata = parsed;
                            } else if (parsed.type === 'done') {
                                returnedSessionId = parsed.session_id || null;
                            } else {
                                const result = handleParsed(parsed);
                                if (result.error) streamError = result.error;
                            }
                        }
                    }
                } finally {
                    this._activeReader = null;
                    reader.releaseLock();
                }

                if (this._streamCancelled) {
                    throw new DOMException('Stream cancelled by user', 'AbortError');
                }

                if (streamError) {
                    throw new Error(streamError);
                }

                // Use content if available, otherwise fall back to complete_message from metadata.
                const finalMessage = content || metadata?.complete_message || '';

                return {
                    message: finalMessage,
                    traceId: traceId,
                    sessionId: returnedSessionId,
                    sources: metadata?.sources ? metadata.sources.map((s: any) => {
                        if (typeof s === 'string') return { url: s };
                        return {
                            url: s.url,
                            filename: s.filename,
                            title: s.title,
                            file_id: s.file_id,
                            type: s.type,
                            is_downloadable: s.is_downloadable
                        };
                    }) : [],
                    relatedLinks: metadata?.related_links ? metadata.related_links.map((r: any) => {
                        if (typeof r === 'string') return { url: r };
                        return {
                            url: r.url,
                            title: r.title || r.filename
                        };
                    }) : [],
                    triggers: metadata?.triggers || { esscalcateCase: false },
                };
            } catch (error) {
                console.error('Failed to submit search:', error);
                throw error;
            }
        },
    },
});
